import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/contexts/AuthContext'
import { LoginAlertProvider } from '@/contexts/LoginAlertContext'
import { ToastProvider } from '@/contexts/ToastContext'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { AppLayout } from '@/components/layout/AppLayout'
import { Spinner } from '@/components/ui/Spinner'
import { routes } from '@/routes'

import { HomePage } from '@/pages/HomePage'
import { SearchPage } from '@/pages/SearchPage'
import { ProductPage } from '@/pages/ProductPage'
import { ShoppingListPage } from '@/pages/ShoppingListPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { LoginPage } from '@/pages/auth/LoginPage'
import { RegisterPage } from '@/pages/auth/RegisterPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { ProfileLayout } from '@/pages/profile/ProfileLayout'
import { ProfileOverviewPage } from '@/pages/profile/ProfileOverviewPage'
import { PersonalDataPage } from '@/pages/profile/PersonalDataPage'
import { SecurityPage } from '@/pages/profile/SecurityPage'

// A área administrativa é carregada sob demanda: a maioria dos visitantes
// nunca a acessa e ela não precisa pesar no bundle inicial.
const AdminProductsPage = lazy(() =>
  import('@/pages/admin/AdminProductsPage').then((m) => ({ default: m.AdminProductsPage })),
)
const AdminProductFormPage = lazy(() =>
  import('@/pages/admin/AdminProductFormPage').then((m) => ({
    default: m.AdminProductFormPage,
  })),
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <LoginAlertProvider>
              <Suspense fallback={<PageFallback />}>
                <Routes>
                  {/* Site público */}
                  <Route element={<AppLayout />}>
                    <Route path={routes.home} element={<HomePage />} />
                    <Route path="/busca" element={<SearchPage />} />
                    <Route path="/categoria/:slug" element={<SearchPage />} />
                    <Route path="/produto/:slug" element={<ProductPage />} />
                    <Route path={routes.shoppingList} element={<ShoppingListPage />} />

                    <Route path={routes.login} element={<LoginPage />} />
                    <Route path={routes.register} element={<RegisterPage />} />
                    <Route path={routes.forgotPassword} element={<ForgotPasswordPage />} />

                    <Route path={routes.profile} element={<ProfileLayout />}>
                      <Route index element={<ProfileOverviewPage />} />
                      <Route path="dados-pessoais" element={<PersonalDataPage />} />
                      <Route path="seguranca" element={<SecurityPage />} />
                    </Route>

                    <Route path="*" element={<NotFoundPage />} />
                  </Route>

                  {/* Administração */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<Navigate to={routes.admin} replace />} />
                    <Route path="produtos" element={<AdminProductsPage />} />
                    <Route path="produtos/novo" element={<AdminProductFormPage />} />
                    <Route path="produtos/:id" element={<AdminProductFormPage />} />
                  </Route>
                </Routes>
              </Suspense>
            </LoginAlertProvider>
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  )
}

function PageFallback() {
  return (
    <div className="flex min-h-dvh items-center justify-center">
      <Spinner size={30} className="text-brand" />
    </div>
  )
}
